import React, { useState } from 'react';
import classnames from 'classnames';
import { useTracking } from 'react-tracking';
import {
  IPageInfo,
  Folder,
  ModerationSearchType,
  ContentType,
  ActionButton,
  IAdvancedFilters,
  MessagesCounts
} from '../../state/types';
import SortTooltip from './sort-tooltip';
import ModerationTagsTooltip from './tag-tooltip';
import MultiSelect, { Props as MultiSelectProps } from './multi-select';
import ModerationSearch from './moderation-search';
import FolderButton from './folder-button';
import { IArticleItem } from '../../../articles/types/articleItem';

interface Props {
  article: IArticleItem;
  pageInfo: IPageInfo;
  counts: MessagesCounts;
  isMultiSelect: boolean;
  searchType: ModerationSearchType;
  searchValue: string;
  isStickyHeader: boolean;
  contentType: ContentType;
  search: (props) => void;
  resetAllFilters: (props?: {
    pageInfo?: Partial<IPageInfo>;
    filters?: Partial<IAdvancedFilters>;
    search?: { searchType: ModerationSearchType; searchValue: string };
  }) => void;
  changePageInfo: (props: Partial<IPageInfo>) => void;
  buttons: ActionButton[];
  numberOfCheckedItems: number;
  numberOfItems: number;
  selectAllItems: () => void;
  clearAllItems: () => void;
  onOpenModal: (modalType: string, modalProps: any) => void;
}

type TProps = Props & MultiSelectProps;

const DashboardHeader: React.FC<TProps> = ({
  article,
  pageInfo,
  counts,
  isMultiSelect,
  searchType,
  searchValue,
  search,
  resetAllFilters,
  changePageInfo,
  buttons = [],
  numberOfCheckedItems,
  numberOfItems,
  selectAllItems,
  clearAllItems,
  onOpenModal,
  isStickyHeader,
  contentType
}) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false);
  const { folder } = pageInfo;
  const tracking = useTracking();

  const className = classnames(
    'filter-container',
    isStickyHeader ? 'sticky-header' : 'static-header'
  );

  const openSearchBar = () => setIsSearchExpanded(true);

  const closeSearchBar = (withReset = false) => {
    setIsSearchExpanded(false);

    // In case we're on search mode, reset all filters to default
    if (withReset && searchValue) {
      resetAllFilters();
    }
  };

  const onFolderButtonClick = (_folder: Folder) => {
    return () => {
      const isActive = pageInfo.folder === _folder;
      if (isActive) {
        resetAllFilters({ pageInfo: { folder: _folder } });
      } else {
        tracking.trackEvent({ eventName: `moderation ${_folder} page clicked` });
        changePageInfo({ folder: _folder });
      }
    };
  };

  return (
    <div className={className}>
      {isMultiSelect ? (
        <MultiSelect
          onOpenModal={onOpenModal}
          numberOfCheckedItems={numberOfCheckedItems}
          buttons={buttons}
          pageInfo={pageInfo}
          numberOfItems={numberOfItems}
          counts={counts}
          selectAllItems={selectAllItems}
          clearAllItems={clearAllItems}
        />
      ) : (
        <div className="dashboard-filter">
          {!isSearchExpanded && (
            <>
              <FolderButton
                label={'Pending'}
                onClick={onFolderButtonClick(Folder.PENDING)}
                isActive={pageInfo.folder === Folder.PENDING}
                count={counts[Folder.PENDING]}
              />
              <FolderButton
                label={'Approved'}
                onClick={onFolderButtonClick(Folder.APPROVED)}
                isActive={pageInfo.folder === Folder.APPROVED}
                count={counts[Folder.APPROVED]}
              />
              <FolderButton
                label={'Rejected'}
                onClick={onFolderButtonClick(Folder.BLOCKED)}
                isActive={pageInfo.folder === Folder.BLOCKED}
                count={counts[Folder.BLOCKED]}
              />
              <FolderButton
                label={'All'}
                onClick={onFolderButtonClick(Folder.ALL)}
                isActive={pageInfo.folder === Folder.ALL}
                count={counts[Folder.ALL]}
              />

              <ModerationTagsTooltip
                pageInfo={pageInfo}
                changePageInfo={changePageInfo}
                contentType={contentType}
              />
              <SortTooltip pageInfo={pageInfo} changePageInfo={changePageInfo} />
            </>
          )}
          <ModerationSearch
            isExpanded={isSearchExpanded}
            openSearchBar={openSearchBar}
            closeSearchBar={closeSearchBar}
            searchType={searchType}
            searchValue={searchValue}
            search={search}
            resetAllFilters={resetAllFilters}
            contentType={contentType}
          />
        </div>
      )}
    </div>
  );
};

export default DashboardHeader;
